/**
 * Music + ambient bed module for Anagram. Mirrors WhatIs audio/music.ts.
 *
 * Beds:
 *   lobby — looping playful pluck pad in lobby
 *   round — looping puzzle underscore during active rounds
 */

import lobbyBedUrl from '../assets/music/lobby-bed.mp3?url';
import roundBedUrl from '../assets/music/round-bed.mp3?url';

import { audioBus } from './bus.js';

export type MusicName = 'lobby' | 'round';

interface MusicSpec { url: string; loop: boolean }

const SPEC_LOBBY: MusicSpec = { url: lobbyBedUrl, loop: true };
const SPEC_ROUND: MusicSpec = { url: roundBedUrl, loop: true };

function specOf(name: MusicName): MusicSpec {
  if (name === 'lobby') return SPEC_LOBBY;
  return SPEC_ROUND;
}

interface Track {
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
}

const tracks = new Map<MusicName, Track>();
let current: { name: MusicName; track: Track } | null = null;

function getTrack(name: MusicName): Track {
  let t = tracks.get(name);
  if (t) return t;
  const spec = specOf(name);
  const el = new Audio(spec.url);
  el.preload = 'auto';
  el.loop = spec.loop;
  let source: MediaElementAudioSourceNode | null = null;
  const ctx = audioBus.context();
  if (ctx) {
    try {
      source = ctx.createMediaElementSource(el);
      source.connect(audioBus.musicGain());
    } catch (err) {
      console.warn(`[an-music] media-element-source failed for ${name}:`, err);
    }
  }
  t = { el, source };
  tracks.set(name, t);
  return t;
}

export const music = {
  play(name: MusicName): void {
    if (current && current.name === name && !current.track.el.paused) return;
    if (current) {
      try { current.track.el.pause(); } catch { /* ignore */ }
    }
    const track = getTrack(name);
    current = { name, track };
    if (!audioBus.isUnlocked()) {
      void audioBus.unlock();
    }
    try {
      track.el.currentTime = 0;
      const p = track.el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          console.debug(`[an-music] autoplay blocked for ${name}:`, err);
        });
      }
    } catch (err) {
      console.warn(`[an-music] play ${name} failed:`, err);
    }
  },
  stop(): void {
    if (!current) return;
    try { current.track.el.pause(); } catch { /* ignore */ }
    current = null;
  },
  resumeIfPending(): void {
    if (!current) return;
    const el = current.track.el;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* still blocked, ignore */ });
      }
    }
  },
};
